import React from "react";

import {
  CheckCircleOutlined,
  DownloadOutlined,
  InboxOutlined,
  TableOutlined,
  UploadOutlined
} from "@ant-design/icons";
import { Alert, Button, Card, Space, Spin, Table, Typography, Upload } from "antd";
import type { UploadProps } from "antd";
import styled from "styled-components";

import type { StickRow } from "../../pad-generator/models/stickData";
import { UploadExcelFileResponse } from "../hooks";
import { ScenarioField } from "../types/field";

const { Title, Text } = Typography;

// Previews can hold tens of thousands of wells; mounting every row exhausts renderer memory
const PREVIEW_PAGINATION = { pageSize: 100, showSizeChanger: false };

// Styled Components
const StyledCard = styled(Card)`
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const EmptyState = styled.div`
  padding: 64px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const DragOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(230, 247, 255, 0.95);
  border: 2px dashed #1890ff;
  border-radius: 8px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DragOverlayContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const StickyTableCell = styled.div`
  position: sticky;
  left: 0;
  background-color: white;
  font-weight: 600;
  min-width: 300px;
  z-index: 1;
`;

const TableContainer = styled.div<{ $isDragging: boolean }>`
  position: relative;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: hidden;

  ${(props) =>
    props.$isDragging &&
    `
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background-color: rgba(24, 144, 255, 0.05);
      border: 2px dashed #1890ff;
      border-radius: 8px;
      z-index: 10;
      pointer-events: none;
    }
  `}
`;

// Types
export interface Well {
  uwid: string;
}

interface TemplatePreviewProps {
  selectedFields: ScenarioField[];
  wells: Well[];
  uploadedData: UploadExcelFileResponse | null;
  isDragging: boolean;
  isDownloading?: boolean;
  isUploading?: boolean;
  uploadError?: string | null;
  stickData?: StickRow[];
  isLoadingStickData?: boolean;
  stickDataError?: Error | null;
  onDownloadTemplate: () => void;
  onFileUpload: (file: File) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  selectedFields,
  wells,
  uploadedData,
  isDragging,
  isDownloading = false,
  isUploading = false,
  uploadError = null,
  stickData,
  isLoadingStickData = false,
  stickDataError = null,
  onDownloadTemplate,
  onFileUpload
}) => {
  const uploadProps: UploadProps = {
    name: "file",
    accept: ".xlsx,.xls",
    showUploadList: false,
    beforeUpload: (file) => {
      onFileUpload(file);
      return false;
    }
  };

  // Table columns
  const columns = [
    {
      title: "Stick Name",
      dataIndex: "id",
      key: "id",
      fixed: "left" as const,
      width: 300,
      render: (text: string) => <StickyTableCell>{text}</StickyTableCell>
    },
    ...selectedFields.map((field) => {
      return {
        title: field.name,
        dataIndex: field.field,
        key: field.field,
        width: 180,
        render: (value: string | number | undefined | { id: string }) => {
          if (value === null || value === undefined || value === "") {
            return <div style={{ textAlign: "center", color: "#d9d9d9" }}>—</div>;
          }
          if (typeof value === "number") {
            return <div style={{ textAlign: "right" }}>{value.toFixed(2)}</div>;
          }
          if (typeof value === "object") {
            return <div></div>;
          }
          return <div>{value}</div>;
        }
      };
    })
  ];

  // Prepare table data source - use stickData if available, otherwise fall back to wells
  const tableDataSource =
    stickData ||
    wells.map((well, index) => ({
      id: well.uwid,
      key: index.toString()
    }));
  return (
    <StyledCard
      title={
        <Space>
          <TableOutlined />
          <span>Template Preview</span>
        </Space>
      }
      extra={
        <Space>
          <Button
            onClick={onDownloadTemplate}
            disabled={selectedFields.length === 0}
            loading={isDownloading}
            icon={<DownloadOutlined />}
            size="large">
            Download Template
          </Button>
          <Upload {...uploadProps}>
            <Button
              disabled={selectedFields.length === 0 || isUploading}
              loading={isUploading}
              icon={<UploadOutlined />}
              type="primary"
              size="large">
              Upload Excel
            </Button>
          </Upload>
        </Space>
      }>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {uploadError && (
          <Alert
            message="Upload Failed"
            description={uploadError}
            type="error"
            showIcon
            closable
          />
        )}

        {uploadedData && !uploadError && (
          <Alert
            message={uploadedData.message ?? "Data uploaded successfully"}
            type="success"
            icon={<CheckCircleOutlined />}
            showIcon
          />
        )}

        {stickDataError && (
          <Alert
            message="Failed to Load Stick Data"
            description={
              stickDataError instanceof Error
                ? stickDataError.message
                : "Failed to fetch stick data from server"
            }
            type="error"
            showIcon
          />
        )}

        {selectedFields.length === 0 ? (
          <EmptyState>
            <TableOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />
            <Title level={4}>No fields selected</Title>
            <Text type="secondary">Select fields to preview your template</Text>
          </EmptyState>
        ) : (
          <TableContainer>
            {isDragging && (
              <DragOverlay>
                <DragOverlayContent>
                  <InboxOutlined style={{ fontSize: 48, color: "#1890ff" }} />
                  <Title level={4} style={{ color: "#1890ff", margin: 0 }}>
                    Drop Excel file here
                  </Title>
                  <Text type="secondary">Release to upload</Text>
                </DragOverlayContent>
              </DragOverlay>
            )}

            <Spin spinning={isLoadingStickData} size="large" tip="Loading stick data...">
              <Table
                columns={columns}
                dataSource={tableDataSource}
                pagination={PREVIEW_PAGINATION}
                scroll={{ x: "max-content" }}
                size="middle"
              />
            </Spin>
          </TableContainer>
        )}
      </Space>
    </StyledCard>
  );
};
